import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getAllUsersPtoHours, getAllUsersNoPagination } from '../../../services/userService';
import { getAllTeams } from '../../../services/teamService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';

const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return value._id ? String(value._id) : null;
    }
    return String(value);
};

const PTOView = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ptoData, setPtoData] = useState([]);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const { user, loading: authLoading } = useAuth();


    // Fetch PTO data when year changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchPtoData = async () => {
            try {
                setDataLoading(true);
                
                console.log('Fetching PTO data for all users:', {
                    year: currentYear,
                    userRole: user?.role
                });
                
                const data = await getAllUsersPtoHours(currentYear);
                if (mounted) {
                    console.log('PTO data received:', {
                        count: data.length,
                        year: currentYear
                    });
                    
                    // Log the first item to see its structure
                    if (data.length > 0) {
                        console.log('Sample PTO data item:', data[0]);
                    }
                    
                    // Filter PTO data based on user role
                    let filteredPtoData = [...data];
                    
                    // If user is a team lead, filter to only show their team members
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering PTO data');

                        try {
                            // Primary source of truth: auth user object already includes teams/teamMembers
                            // populated by backend (/api/auth/me + auth middleware).
                            const teamMemberIdsFromAuth = (user.teamMembers || []).map(String);
                            const authTeams = Array.isArray(user.teams) ? user.teams : Array.isArray(user.ledTeams) ? user.ledTeams : [];

                            // Always include the lead themselves.
                            const effectiveTeamMemberIds = [...new Set([
                                ...teamMemberIdsFromAuth,
                                String(user.id)
                            ])];

                            // Optional: fall back to fetching teams ONLY if we don't have team member ids.
                            // Note: /api/teams is admin-only in the backend, so this will often fail for team leads.
                            let leadTeams = authTeams;
                            if (effectiveTeamMemberIds.length <= 1) {
                                try {
                                    const teams = await getAllTeams();
                                    console.log('All teams (from API):', teams);

                                    const userId = String(user.id);
                                    leadTeams = teams.filter(team => {
                                        const leadId = normalizeId(team.teamLead);
                                        return leadId && leadId === userId;
                                    });

                                    if (leadTeams.length > 0) {
                                        const idsFromTeams = leadTeams.flatMap(team =>
                                            (team.members || []).map(m => normalizeId(m)).filter(Boolean)
                                        );

                                        idsFromTeams.forEach(id => effectiveTeamMemberIds.push(String(id)));
                                    }
                                } catch (teamsErr) {
                                    console.warn('Unable to fetch teams (expected for non-admin). Falling back to auth teamMembers.', teamsErr);
                                }
                            }

                            const finalTeamMemberIds = [...new Set(effectiveTeamMemberIds)];

                            console.log('Teams where user is team lead (authTeams):', leadTeams);
                            console.log('Team member IDs (effective):', finalTeamMemberIds);

                            if (finalTeamMemberIds.length > 1) {
                                // Get all users
                                const allUsers = await getAllUsersNoPagination();
                                console.log('All users:', allUsers);

                                // Filter users to only include those in the team lead's teams
                                const teamMembers = allUsers.filter(u =>
                                    finalTeamMemberIds.includes(String(u._id))
                                );

                                console.log('Team members:', teamMembers);

                                // Get the usernames of the team members
                                const teamMemberUsernames = teamMembers.map(u => u.username);
                                console.log('Team member usernames:', teamMemberUsernames);

                                // Filter PTO data to only show those for team members
                                filteredPtoData = filteredPtoData.filter(item =>
                                    teamMemberUsernames.includes(item.username)
                                );

                                console.log('Filtered PTO data count:', filteredPtoData.length);
                            } else {
                                // If we can't resolve any team members, show only the team lead.
                                console.log('No team members resolved, showing only their own PTO data');
                                filteredPtoData = filteredPtoData.filter(item =>
                                    item.username === user.username
                                );
                                console.log('Filtered PTO data count (self only):', filteredPtoData.length);
                            }
                        } catch (err) {
                            console.error('Error filtering PTO data:', err);
                            // Fail closed: team leads should never see all users.
                            filteredPtoData = filteredPtoData.filter(item =>
                                item.username === user.username
                            );
                        }
                    }
                    
                    setPtoData(filteredPtoData);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching PTO data:', err);
                if (mounted) {
                    setError(err.message);
                    setPtoData([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user and they are admin/team lead/accountant
        if (user && (user.role === 'admin' || user.role === 'team_lead' || user.role === 'accountant')) {
            fetchPtoData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentYear, user]);

    const handlePreviousYear = () => {
        setCurrentYear(prev => prev - 1);
    };

    const handleNextYear = () => {
        setCurrentYear(prev => prev + 1);
    };


    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data
    if (!user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">Authentication required</Alert>
            </Box>
        );
    }

    // Prepare data for export
    const exportData = ptoData.map(item => ({
        Year: currentYear,
        Username: item.username,
        'Year Start': item.yearStart,
        'YTD Accumulated': item.ytdAccumulated,
        'YTD Used': item.ytdUsed,
        Available: item.available
    }));

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    PTO Report - All Users
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire year navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousYear}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                            '@media (max-width:900px) and (orientation: landscape)': {
                                fontSize: '0.7rem', // Even smaller font in landscape
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2, // Tighter line height
                            }
                        }}>
                            {currentYear}
                        </Typography>
                        <IconButton 
                            onClick={handleNextYear}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="pto"
                        timecards={ptoData}
                        isAllUsers={true}
                        viewMode="allUsers"
                        date={new Date(currentYear, 0, 1)}
                        user={{ username: 'all_users' }}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* PTO Data Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>Year</TableCell>
                            <TableCell sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>Username</TableCell>
                            <TableCell align="right" sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>Year Start</TableCell>
                            <TableCell align="right" sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>YTD Accumulated</TableCell>
                            <TableCell align="right" sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>YTD Used</TableCell>
                            <TableCell align="right" sx={{
                                '@media (max-width:900px)': {
                                    padding: '8px',
                                    fontSize: '0.75rem',
                                }
                            }}>Available</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ptoData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No PTO data found for this year
                                </TableCell>
                            </TableRow>
                        ) : (
                            ptoData.map((item) => (
                                <TableRow key={item._id}>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>{currentYear}</TableCell>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>{item.username}</TableCell>
                                    <TableCell align="right" sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>{parseFloat(item.yearStart).toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>{parseFloat(item.ytdAccumulated).toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>{parseFloat(item.ytdUsed).toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}>{parseFloat(item.available).toFixed(2)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default PTOView;
